import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Crosshair, Trophy, Loader2, Swords, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import { useLanguage, api } from '../context/AppContext';
import { BadgeCelebration } from './BadgeCelebration';

export function TacticalWeeklyChallenge() {
  const { language } = useLanguage();
  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [celebrationBadge, setCelebrationBadge] = useState(null);

  const fetchChallenge = useCallback(async () => {
    try {
      const res = await api.get('/challenges/tactical-weekly');
      if (res.data.active) {
        setChallenge(res.data.challenge);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchChallenge(); }, [fetchChallenge]);

  // Check for newly validated challenge (from exercise log response)
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.tactical_challenge_validated?.validated) {
        const v = e.detail.tactical_challenge_validated;
        setChallenge(prev => prev ? { ...prev, validated: true, validated_at: new Date().toISOString() } : prev);
        setCelebrationBadge({
          name: language === 'fr' ? 'DÉFI TACTIQUE VALIDÉ' : 'TACTICAL CHALLENGE COMPLETE',
          description: language === 'fr'
            ? `PR abattu sur ${v.exercise}! +${v.bonus_points} pts pour ton equipe!`
            : `PR crushed on ${v.exercise}! +${v.bonus_points} pts for your team!`,
          icon: '🎯',
          rarity: 'legendary',
        });
      }
    };
    window.addEventListener('tactical-challenge-validated', handler);
    return () => window.removeEventListener('tactical-challenge-validated', handler);
  }, [language]);

  if (loading || !challenge) return null;
  if (challenge.expired && !challenge.validated) return null;

  const daysLeft = () => {
    if (!challenge.expires_at) return '';
    const exp = new Date(challenge.expires_at).getTime();
    const now = Date.now();
    const days = Math.max(0, Math.ceil((exp - now) / (1000 * 60 * 60 * 24)));
    return days;
  };

  const remaining = daysLeft();

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className={`mb-4 rounded-2xl border-2 overflow-hidden ${
          challenge.validated
            ? 'border-emerald-500/40 bg-gradient-to-r from-emerald-500/10 to-green-500/5'
            : 'border-red-500/40 bg-gradient-to-r from-red-500/10 to-orange-500/5'
        }`}
        data-testid="tactical-weekly-challenge"
      >
        {/* Header */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full p-4 flex items-center justify-between"
          data-testid="tactical-challenge-toggle"
        >
          <div className="flex items-center gap-3">
            <motion.div
              animate={challenge.validated ? { scale: [1, 1.2, 1] } : { rotate: [0, -5, 5, 0] }}
              transition={{ repeat: Infinity, duration: challenge.validated ? 2 : 3 }}
            >
              {challenge.validated
                ? <Trophy className="w-6 h-6 text-emerald-400" />
                : <Crosshair className="w-6 h-6 text-red-400" />
              }
            </motion.div>
            <div className="text-left">
              <p className={`text-[10px] uppercase tracking-[0.3em] font-bold ${
                challenge.validated ? 'text-emerald-400' : 'text-red-400'
              }`}>
                {challenge.validated
                  ? (language === 'fr' ? 'Défi Valide' : 'Challenge Validated')
                  : (language === 'fr' ? 'Défi Tactique de la Semaine' : 'Weekly Tactical Challenge')
                }
              </p>
              <p className="text-sm font-heading uppercase tracking-tight text-foreground mt-0.5">
                {challenge.exercise_display}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!challenge.validated && (
              <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {remaining}j
              </span>
            )}
            {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
          </div>
        </button>

        {/* Content */}
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="px-4 pb-4 space-y-3">
                {/* Order from Coach Yuri */}
                <div className={`p-3 rounded-xl border ${
                  challenge.validated
                    ? 'border-emerald-500/20 bg-emerald-500/5'
                    : 'border-red-500/20 bg-red-500/5'
                }`} data-testid="tactical-challenge-order">
                  <p className="text-xs text-foreground/90 leading-relaxed">
                    {challenge.validated ? (
                      language === 'fr'
                        ? `Objectif atteint. Tu as domine ${challenge.exercise_display}. +${challenge.bonus_points} pts pour ton equipe. C'est ca, un athlete d'elite.`
                        : `Mission accomplished. Target eliminated on ${challenge.exercise_display}. +${challenge.bonus_points} pts for your team. That's an elite soldier.`
                    ) : (
                      language === 'fr'
                        ? `YUMIX a identifie ta cible prioritaire${challenge.reason === 'stagnation' ? ' (plateau détecté)' : ''} : ${
                          challenge.challenge_type === 'weight'
                            ? `Ajoute +${challenge.target_value - challenge.current_value} ${challenge.target_unit} sur ton ${challenge.exercise_display}. Cible a abattre : ${challenge.target_label}.`
                            : `Monte a ${challenge.target_label} sur ton ${challenge.exercise_display}. Le volume forge la puissance.`
                        }`
                        : `YUMIX identified your priority target${challenge.reason === 'stagnation' ? ' (plateau detected)' : ''}: ${
                          challenge.challenge_type === 'weight'
                            ? `Add +${challenge.target_value - challenge.current_value} ${challenge.target_unit} to your ${challenge.exercise_display}. Target: ${challenge.target_label}.`
                            : `Hit ${challenge.target_label} on your ${challenge.exercise_display}. Volume forges power.`
                        }`
                    )}
                  </p>
                </div>

                {/* Stats bar */}
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <Swords className={`w-4 h-4 ${challenge.validated ? 'text-emerald-400' : 'text-red-400'}`} />
                    <span className="text-[10px] text-muted-foreground uppercase tracking-wider">
                      {language === 'fr' ? 'Bonus Equipe' : 'Team Bonus'}:
                    </span>
                    <span className={`text-sm font-black ${challenge.validated ? 'text-emerald-400' : 'text-red-400'}`}>
                      +{challenge.bonus_points} pts
                    </span>
                  </div>

                  {/* Progress indicator */}
                  <div className={`px-3 py-1 rounded-full text-[10px] font-heading uppercase tracking-wider ${
                    challenge.validated
                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                      : 'bg-red-500/20 text-red-400 border border-red-500/30'
                  }`} data-testid="tactical-challenge-status">
                    {challenge.validated
                      ? (language === 'fr' ? 'Cible Abattue' : 'Target Down')
                      : (language === 'fr' ? 'En Cours' : 'Active')
                    }
                  </div>
                </div>

                {/* Target display */}
                {!challenge.validated && (
                  <div className="flex items-center justify-center gap-4 p-3 bg-card/40 rounded-xl border border-border/50">
                    <div className="text-center">
                      <p className="text-[9px] text-muted-foreground uppercase">{language === 'fr' ? 'Actuel' : 'Current'}</p>
                      <p className="text-lg font-black text-foreground">
                        {challenge.current_value} <span className="text-xs text-muted-foreground">{challenge.target_unit}</span>
                      </p>
                    </div>
                    <motion.div
                      animate={{ x: [0, 5, 0] }}
                      transition={{ repeat: Infinity, duration: 1.5 }}
                      className="text-red-400 font-black text-lg"
                    >
                      &rarr;
                    </motion.div>
                    <div className="text-center">
                      <p className="text-[9px] text-red-400 uppercase font-bold">{language === 'fr' ? 'Cible' : 'Target'}</p>
                      <p className="text-lg font-black text-red-400">
                        {challenge.target_value} <span className="text-xs">{challenge.target_unit}</span>
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {celebrationBadge && (
        <BadgeCelebration
          badge={celebrationBadge}
          onClose={() => setCelebrationBadge(null)}
          language={language}
        />
      )}
    </>
  );
}
